import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AdaptationView,
  CanonicalPeriod,
  EmissionTier,
  parseCanonicalPeriod,
  parseAdaptationView,
  ADAPTATION_VIEW_DEFAULT,
} from './index'
import { getClimateHazardViewConfig } from './hazardConfig'

type ClimateHazardRouteItem = {
  name: string
}

type ClimateAnalyticsNavigation = {
  push: (path: string) => void
  replace: (path: string) => void
}

type UseClimateAnalyticsRouteStateArgs = {
  hazards: ClimateHazardRouteItem[]
  hazardFromRoute?: string
  pathname: string
  search: string
  navigation: ClimateAnalyticsNavigation
  buildPath: (hazardCode: string, emissionTier: EmissionTier, period: CanonicalPeriod, view: AdaptationView) => string
}

export function parseEmissionTier(value: string | null): EmissionTier | null {
  if (value === EmissionTier.LOW || value === EmissionTier.MODERATE || value === EmissionTier.HIGH) {
    return value
  }
  return null
}

export function parseEmissionTierFromSearch(search: string): EmissionTier | null {
  return parseEmissionTier(new URLSearchParams(search).get('emission'))
}

export function parseCanonicalPeriodFromSearch(search: string): CanonicalPeriod | null {
  return parseCanonicalPeriod(new URLSearchParams(search).get('period'))
}

export function parseAdaptationViewFromSearch(search: string): AdaptationView | null {
  return parseAdaptationView(new URLSearchParams(search).get('view'))
}

export function resolvePeriodForHazard(
  hazardCode: string | null,
  candidate: CanonicalPeriod | null | undefined,
): CanonicalPeriod {
  const config = getClimateHazardViewConfig(hazardCode)
  if (candidate && config.availablePeriods.includes(candidate)) return candidate
  return config.defaultPeriod
}

export function useClimateAnalyticsRouteState({
  hazards,
  hazardFromRoute,
  pathname,
  search,
  navigation,
  buildPath,
}: UseClimateAnalyticsRouteStateArgs) {
  const [emissionTier, setEmissionTier] = useState<EmissionTier>(
    () => parseEmissionTierFromSearch(search) ?? EmissionTier.HIGH,
  )
  const [selectedPeriod, setSelectedPeriod] = useState<CanonicalPeriod>(
    () => parseCanonicalPeriodFromSearch(search) ?? CanonicalPeriod.LONG_TERM,
  )
  const [adaptationView, setAdaptationView] = useState<AdaptationView>(
    () => parseAdaptationViewFromSearch(search) ?? ADAPTATION_VIEW_DEFAULT,
  )
  const [selectedHazardCode, setSelectedHazardCode] = useState<string | null>(null)

  const emissionFromQuery = useMemo(() => parseEmissionTierFromSearch(search), [search])
  const periodFromQuery = useMemo(() => parseCanonicalPeriodFromSearch(search), [search])
  const viewFromQuery = useMemo(() => parseAdaptationViewFromSearch(search), [search])

  const currentHazardCode = useMemo(
    () =>
      selectedHazardCode ?? hazards.find(hazard => hazard.name === hazardFromRoute)?.name ?? hazards[0]?.name ?? null,
    [hazardFromRoute, hazards, selectedHazardCode],
  )

  const hazardViewConfig = useMemo(() => getClimateHazardViewConfig(selectedHazardCode), [selectedHazardCode])

  useEffect(() => {
    if (!hazards.length) return

    const fallbackHazard = hazards[0]?.name ?? null
    const nextHazard = hazards.find(hazard => hazard.name === hazardFromRoute)?.name ?? fallbackHazard
    if (!nextHazard) return

    const nextEmissionTier = emissionFromQuery ?? EmissionTier.HIGH
    const nextPeriod = resolvePeriodForHazard(nextHazard, periodFromQuery)
    const nextView = viewFromQuery ?? ADAPTATION_VIEW_DEFAULT

    if (selectedHazardCode !== nextHazard) {
      setSelectedHazardCode(nextHazard)
    }
    if (emissionTier !== nextEmissionTier) {
      setEmissionTier(nextEmissionTier)
    }
    if (selectedPeriod !== nextPeriod) {
      setSelectedPeriod(nextPeriod)
    }
    if (adaptationView !== nextView) {
      setAdaptationView(nextView)
    }

    const targetPath = buildPath(nextHazard, nextEmissionTier, nextPeriod, nextView)
    const currentPath = `${pathname}${search}`
    if (currentPath !== targetPath) {
      navigation.replace(targetPath)
    }
  }, [
    buildPath,
    emissionFromQuery,
    emissionTier,
    hazards,
    hazardFromRoute,
    navigation,
    pathname,
    periodFromQuery,
    search,
    selectedHazardCode,
    selectedPeriod,
    adaptationView,
    viewFromQuery,
  ])

  const handleHazardChange = useCallback(
    (nextHazard: string) => {
      const nextPeriod = resolvePeriodForHazard(nextHazard, selectedPeriod)
      setSelectedHazardCode(nextHazard)
      setSelectedPeriod(nextPeriod)
      navigation.push(buildPath(nextHazard, emissionTier, nextPeriod, adaptationView))
    },
    [buildPath, emissionTier, navigation, selectedPeriod, adaptationView],
  )

  const handleEmissionTierChange = useCallback(
    (nextEmissionTier: EmissionTier) => {
      if (!currentHazardCode) return
      setEmissionTier(nextEmissionTier)
      navigation.push(buildPath(currentHazardCode, nextEmissionTier, selectedPeriod, adaptationView))
    },
    [buildPath, currentHazardCode, navigation, selectedPeriod, adaptationView],
  )

  const handlePeriodChange = useCallback(
    (nextPeriod: CanonicalPeriod) => {
      if (!currentHazardCode) return
      const resolvedPeriod = resolvePeriodForHazard(currentHazardCode, nextPeriod)
      setSelectedPeriod(resolvedPeriod)
      navigation.push(buildPath(currentHazardCode, emissionTier, resolvedPeriod, adaptationView))
    },
    [buildPath, currentHazardCode, emissionTier, navigation, adaptationView],
  )

  const handleAdaptationViewChange = useCallback(
    (nextView: AdaptationView) => {
      if (!currentHazardCode) return
      navigation.push(buildPath(currentHazardCode, emissionTier, selectedPeriod, nextView))
    },
    [buildPath, currentHazardCode, emissionTier, navigation, selectedPeriod],
  )

  return {
    currentHazardCode,
    emissionTier,
    adaptationView,
    handleAdaptationViewChange,
    handleEmissionTierChange,
    handleHazardChange,
    handlePeriodChange,
    hazardViewConfig,
    selectedHazardCode,
    selectedPeriod,
  }
}
